import { useLayoutEffect, useRef, useState } from 'react';
import type { TextFieldProps } from '@mui/material/TextField';
import TextField from './FormTextField';
import { exceedsPhoneInputLimit, formatPhoneInput, getPhoneDigits, isValidPhone, phoneCaretPosition, PHONE_INPUT_LIMIT_ERROR } from '../../shared/phoneInput';

type PhoneTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (value: string) => void;
  invalidPhoneMessage: string;
  inputLimitMessage?: string;
};

/** A phone mask that formats input without imposing a destructive digit limit. */
export default function PhoneTextField({
  value, onValueChange, invalidPhoneMessage, inputLimitMessage = PHONE_INPUT_LIMIT_ERROR, error, helperText, onBlur, onKeyDown, ...props
}: PhoneTextFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const [blurred, setBlurred] = useState(false);
  const [limitExceeded, setLimitExceeded] = useState(false);
  const formatted = formatPhoneInput(value);
  const hasInvalidPhone = !!value && !isValidPhone(value)
    && (blurred || getPhoneDigits(value).length > 10);

  useLayoutEffect(() => {
    if (pendingCaret.current !== null) {
      inputRef.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
    if (!value) setBlurred(false);
    setLimitExceeded(false);
  }, [value]);

  useLayoutEffect(() => {
    // A rejected replacement must also block saving the previous phone value.
    inputRef.current?.setCustomValidity(limitExceeded ? inputLimitMessage : '');
  }, [limitExceeded, inputLimitMessage]);

  return (
    <TextField
      {...props}
      type="tel"
      inputRef={inputRef}
      inputProps={{ inputMode: 'tel', ...props.inputProps }}
      value={formatted}
      error={!!error || hasInvalidPhone || limitExceeded}
      helperText={limitExceeded ? inputLimitMessage : hasInvalidPhone ? invalidPhoneMessage : helperText}
      onBlur={(event) => {
        setBlurred(true);
        onBlur?.(event);
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        const input = inputRef.current;
        if (!input || input.selectionStart !== input.selectionEnd) return;
        const caret = input.selectionStart ?? 0;
        // Backspace/Delete should reach the adjacent digit across a mask space.
        if (event.key === 'Backspace' && input.value[caret - 1] === ' ') {
          input.setSelectionRange(caret - 1, caret - 1);
        } else if (event.key === 'Delete' && input.value[caret] === ' ') {
          input.setSelectionRange(caret + 1, caret + 1);
        }
      }}
      onChange={(event) => {
        const input = event.target;
        const raw = input.value;
        const caret = input.selectionStart ?? raw.length;
        if (exceedsPhoneInputLimit(raw)) {
          // Reject the entire edit; never keep a truncated portion of a paste.
          input.value = formatted;
          input.setSelectionRange(Math.min(caret, formatted.length), Math.min(caret, formatted.length));
          pendingCaret.current = null;
          setLimitExceeded(true);
          return;
        }
        setLimitExceeded(false);
        const digitsBeforeCaret = getPhoneDigits(raw.slice(0, caret)).length;
        const next = formatPhoneInput(raw);
        const nextCaret = phoneCaretPosition(next, digitsBeforeCaret);

        // Apply even when only punctuation changed and the parent won't rerender.
        input.value = next;
        input.setSelectionRange(nextCaret, nextCaret);
        pendingCaret.current = nextCaret;
        onValueChange(next);
      }}
    />
  );
}
